import ImageOutlinedIcon from "@mui/icons-material/ImageOutlined";
import {
	Box,
	Button,
	CircularProgress,
	Paper,
	Stack,
	Typography,
} from "@mui/material";
import { PDFDocument } from "pdf-lib";
import { useEffect, useRef, useState } from "react";

import { isSectionSpecPdf } from "@/shared/models/SectionSpecUpload";
import { useObjectUrl } from "@/shared/hooks/useObjectUrl";
import { useSectionSpecDrawing } from "../../hooks/useSectionSpecDrawing";
import {
	applySectionSpecRotation,
	type SectionSpecFileChange,
	type SectionSpecRotation,
} from "../../models/SectionSpecRotation";
import {
	ROTATION_CONTROLS_HOVER_SX,
	SectionSpecRotationControls,
} from "./SectionSpecRotationControls";

interface SectionSpecFileSelectionProps {
	disabled: boolean;
	change: SectionSpecFileChange | null;
	fillHeight?: boolean;
	label: string;
	rotateLabel: string;
	onChange: (change: SectionSpecFileChange | null) => void;
	onError: (error: Error) => void;
	persistedFileId: number | null;
	persistedFileName: string | null;
}

export const SectionSpecFileSelection = ({
	disabled,
	change,
	fillHeight = false,
	label,
	rotateLabel,
	onChange,
	onError,
	persistedFileId,
	persistedFileName,
}: SectionSpecFileSelectionProps) => {
	const inputRef = useRef<HTMLInputElement>(null);
	const [rotating, setRotating] = useState(false);

	const {
		data: persistedPreviewData,
		isFetching,
		isError,
	} = useSectionSpecDrawing(
		persistedFileId,
		persistedFileName,
		change?.source == null,
	);

	const file = change?.file ?? null;
	const persisted =
		persistedPreviewData && persistedFileName
			? { blob: persistedPreviewData, fileName: persistedFileName }
			: null;
	const canRotate = change?.source != null || persisted !== null;

	const rotate = async (rotation: SectionSpecRotation) => {
		setRotating(true);
		try {
			onChange(await applySectionSpecRotation(change, persisted, rotation));
		} catch (error) {
			onError(
				error instanceof Error
					? error
					: new Error("The file could not be rotated"),
			);
		} finally {
			setRotating(false);
		}
	};

	const displayedBlob = file ?? persistedPreviewData ?? null;
	const objectUrl = useObjectUrl(displayedBlob);
	const displayedFileName = file?.name ?? persistedFileName;
	const isPdf = isSectionSpecPdf(
		displayedFileName,
		file?.type ?? persistedPreviewData?.type,
	);
	const [pdfAspect, setPdfAspect] = useState<{
		blob: Blob;
		ratio: number;
	} | null>(null);

	useEffect(() => {
		if (!isPdf || !displayedBlob) return;
		let cancelled = false;

		const readAspectRatio = async () => {
			try {
				const document = await PDFDocument.load(
					await displayedBlob.arrayBuffer(),
					{ ignoreEncryption: true },
				);
				const page = document.getPages()[0];
				if (!page) return;
				const { width, height } = page.getCropBox();
				const rotation = Math.abs(page.getRotation().angle) % 180;
				const ratio = rotation === 90 ? height / width : width / height;
				if (!cancelled && width > 0 && height > 0 && Number.isFinite(ratio)) {
					setPdfAspect({ blob: displayedBlob, ratio });
				}
			} catch {
				// Keep the full-panel preview when PDF metadata cannot be read.
			}
		};
		void readAspectRatio();
		return () => {
			cancelled = true;
		};
	}, [displayedBlob, isPdf]);

	// Blob identity avoids reusing a persisted file's ratio after upload or rotation.
	const aspectRatio =
		pdfAspect?.blob === displayedBlob ? pdfAspect.ratio : null;
	const fitsPdfPage = isPdf && aspectRatio !== null;

	return (
		<Paper
			variant="outlined"
			sx={{
				height: "100%",
				minHeight: 0,
				overflow: "hidden",
				display: fillHeight ? "flex" : undefined,
				flexDirection: "column",
			}}
		>
			<input
				ref={inputRef}
				type="file"
				disabled={disabled}
				accept="image/*,application/pdf"
				hidden
				onChange={(event) => {
					const selected = event.target.files?.[0] ?? null;
					onChange(
						selected === null
							? null
							: { source: selected, angle: 0, file: selected },
					);
					event.target.value = "";
				}}
			/>
			<Stack spacing={0.5} sx={{ p: 1, flexShrink: 0 }}>
				<Button
					variant="outlined"
					startIcon={<ImageOutlinedIcon />}
					disabled={disabled}
					fullWidth
					onClick={() => inputRef.current?.click()}
				>
					{label}
				</Button>
				<Typography
					variant="caption"
					color="text.secondary"
					noWrap
					title={displayedFileName ?? "No persisted file"}
				>
					{displayedFileName ?? "No persisted file"}
				</Typography>
			</Stack>
			<Box
				sx={{
					...ROTATION_CONTROLS_HOVER_SX,
					alignItems: "center",
					containerType: fitsPdfPage ? "size" : "normal",
					aspectRatio: fillHeight ? { xs: "4 / 3", md: "auto" } : "4 / 3",
					flex: fillHeight ? { md: 1 } : undefined,
					minHeight: 0,
					bgcolor: "action.hover",
					display: "flex",
					justifyContent: "center",
					overflow: "hidden",
					position: "relative",
				}}
			>
				{objectUrl && (
					<SectionSpecRotationControls
						label={rotateLabel}
						rotation={{
							canRotate: !disabled && canRotate,
							change,
							rotating,
							saving: false,
							rotate: (rotation) => void rotate(rotation),
						}}
					/>
				)}
				{objectUrl && isPdf ? (
					<Box
						component="iframe"
						src={`${objectUrl}#toolbar=0&view=Fit`}
						title={`${label} PDF preview`}
						sx={
							fitsPdfPage
								? {
										aspectRatio,
										border: 0,
										height: "auto",
										width: `min(100%, calc(100cqh * ${aspectRatio}))`,
									}
								: {
										border: 0,
										height: "100%",
										width: "100%",
										position: fillHeight ? "absolute" : undefined,
										inset: fillHeight ? 0 : undefined,
									}
						}
					/>
				) : objectUrl ? (
					<Box
						component="img"
						src={objectUrl}
						alt={`${label} preview`}
						sx={{
							height: "100%",
							objectFit: "contain",
							width: "100%",
							position: fillHeight ? "absolute" : undefined,
							inset: fillHeight ? 0 : undefined,
						}}
					/>
				) : isFetching ? (
					<CircularProgress size={28} />
				) : (
					<Stack
						alignItems="center"
						spacing={0.5}
						sx={{ color: "text.disabled" }}
					>
						<ImageOutlinedIcon sx={{ fontSize: 40 }} />
						<Typography variant="caption" color="inherit">
							{isError ? "Preview unavailable" : "No file persisted"}
						</Typography>
					</Stack>
				)}
			</Box>
		</Paper>
	);
};
